// Section 3: world generation. People with a realistic age pyramid (~20% children, 60% adults,
// 20% elders), at least two adults of each sex, at least one adult with farming skill 2+,
// at most two traits, neutral names; households of 2-5; starting buildings and stocks.
// Job assignment and capacities need content, so api/new-game.ts finishes the state.
import type { Config } from '../config';
import { clamp } from './math';
import { emptyIdCounters, mintId } from './ids';
import { createRng, seedStreams, type Rng } from './rng';
import { formHouseholds } from './generate-households';
import {
  emptyLedger,
  emptyNorms,
  emptyOverrides,
  emptyResourceHistory,
  emptySeries,
  perRationResource,
  perResource,
} from './state-init';
import type {
  ArrivalGroup,
  ArrivalSpec,
  BuildingTypeId,
  GameState,
  NewGameOptions,
  Person,
  PersonId,
  Sex,
  SkillId,
  TraitId,
} from './types';

export interface NameLists {
  female: readonly string[];
  male: readonly string[];
}

export interface GenerateInput {
  cfg: Config;
  options: NewGameOptions;
  seed: string;
  names: NameLists;
}

type AgeBand = 'child' | 'adult' | 'elder';

function ageSeasonsFor(cfg: Config, band: AgeBand, rng: Rng): number {
  const g = cfg.newGame;
  const [lo, hi] = (
    band === 'child' ? g.childAgeYears : band === 'adult' ? g.adultAgeYears : g.elderAgeYears
  ) as [number, number];
  // Children uniform; adults and elders skewed young (min of two uniforms).
  const u = band === 'child' ? rng.float() : Math.min(rng.float(), rng.float());
  const years = lo + Math.floor(u * (hi - lo + 1));
  return years * cfg.people.seasonsPerYear + rng.int(0, cfg.people.seasonsPerYear - 1);
}

function nameFor(
  sex: Sex,
  pools: Record<Sex, string[]>,
  used: Set<string>,
  rng: Rng,
  names: NameLists,
): string {
  const pool = pools[sex];
  while (pool.length > 0) {
    const name = pool.pop() as string;
    if (!used.has(name)) {
      used.add(name);
      return name;
    }
  }
  const base = rng.pick(sex === 'female' ? names.female : names.male);
  let n = 2;
  while (used.has(`${base} ${n}`)) n++;
  used.add(`${base} ${n}`);
  return `${base} ${n}`;
}

function pickTraits(cfg: Config, rng: Rng): TraitId[] {
  const weights = cfg.newGame.traitCountWeights;
  const count = rng.weighted(weights.map((weight, item) => ({ item, weight })));
  const all = Object.keys(cfg.traits) as TraitId[];
  const out: TraitId[] = [];
  for (const trait of rng.shuffle(all)) {
    if (out.length >= Math.min(count, cfg.people.maxTraits)) break;
    const clash = cfg.newGame.exclusiveTraitPairs.some(
      ([a, b]) => (trait === a && out.includes(b)) || (trait === b && out.includes(a)),
    );
    if (!clash) out.push(trait);
  }
  return out.sort();
}

function pickSkills(
  cfg: Config,
  options: NewGameOptions,
  band: AgeBand,
  rng: Rng,
): Partial<Record<SkillId, number>> {
  if (band === 'child') return {};
  const k = cfg.newGame.knowledge[options.startingKnowledge];
  const all = (Object.keys(cfg.jobs) as (keyof Config['jobs'])[]).filter(
    (j): j is SkillId => j !== 'rest',
  );
  const count = rng.int(k.skillsMin, k.skillsMax);
  const bonus = band === 'elder' ? cfg.newGame.knowledge.elderBonus : 0;
  const skills: Partial<Record<SkillId, number>> = {};
  for (const skill of cfg.newGame.knowledge.commonSkills) skills[skill] = k.base;
  for (const skill of rng.shuffle(all).slice(0, count)) {
    const level = k.levelMin + rng.float() * (k.levelMax - k.levelMin) + bonus;
    skills[skill] = Math.max(
      skills[skill] ?? 0,
      Math.round(clamp(level, 0, cfg.skills.max) * 10) / 10,
    );
  }
  return skills;
}

function makePerson(
  cfg: Config,
  state: GameState,
  input: GenerateInput,
  band: AgeBand,
  rng: Rng,
): Person {
  const st = cfg.newGame.stats;
  const ageSeasons = ageSeasonsFor(cfg, band, rng);
  const strengthMean =
    band === 'child' ? st.strengthChild : band === 'adult' ? st.strengthAdult : st.strengthElder;
  const skills = pickSkills(cfg, input.options, band, rng);
  return {
    id: mintId(state, 'person'),
    name: '',
    sex: rng.happened(rng.chance(0.5)) ? 'female' : 'male',
    ageSeasons,
    health: Math.round(
      clamp(rng.normal(st.healthMean, st.healthSd), st.healthMin, cfg.people.statMax),
    ),
    strength: Math.round(
      clamp(rng.normal(strengthMean, st.strengthSd), cfg.bodies.strengthFloor, cfg.people.statMax),
    ),
    happiness: Math.round(
      clamp(rng.normal(st.happinessMean, st.happinessSd), 0, cfg.people.statMax),
    ),
    trust: Math.round(clamp(rng.normal(st.trustMean, st.trustSd), 0, cfg.people.statMax)),
    skills,
    skillPeaks: { ...skills },
    skillLastUsed: {},
    traits: pickTraits(cfg, rng),
    status: {
      sick: null,
      injured: null,
      pregnant: null,
      inSchool: false,
      inCustody: null,
      grieving: null,
      stunted: false,
      literate: false,
      guest: null,
      restitution: null,
      quarantined: false,
    },
    householdId: 'h0000',
    partnerId: null,
    parentIds: [],
    childIds: [],
    memory: [],
    job: 'rest',
    masterId: null,
    streaks: {
      hungry: 0,
      thirsty: 0,
      cold: 0,
      exposed: 0,
      fed: 0,
      childhoodHungry: 0,
      emigration: 0,
      idle: 0,
      inJob: 0,
      school: 0,
    },
    origin: 'founder',
    bornTurn: -ageSeasons,
    arrivedTurn: null,
    plannedDeparture: null,
    offences: 0,
  };
}

/** Section 3 constraints: two adults of each sex, one adult farmer at skill 2+. */
function enforceConstraints(cfg: Config, adults: Person[], rng: Rng): void {
  const g = cfg.newGame;
  for (const sex of ['female', 'male'] as const) {
    const other: Sex = sex === 'female' ? 'male' : 'female';
    while (adults.filter((p) => p.sex === sex).length < g.minAdultsPerSex) {
      const flip = adults.find((p) => p.sex === other);
      if (flip === undefined) break;
      flip.sex = sex;
    }
  }
  const farming = g.farmingSkill;
  if (!adults.some((p) => (p.skills[farming] ?? 0) >= g.minFarmingSkill)) {
    const best = adults.reduce((a, b) =>
      (b.skills[farming] ?? 0) > (a.skills[farming] ?? 0) ? b : a,
    );
    const level = Math.round((g.minFarmingSkill + rng.float()) * 10) / 10;
    best.skills[farming] = level;
    best.skillPeaks[farming] = Math.max(best.skillPeaks[farming] ?? 0, level);
  }
}

function bandCounts(cfg: Config, n: number): Record<AgeBand, number> {
  const minAdults = cfg.newGame.minAdultsPerSex * 2;
  let child = Math.round(n * cfg.newGame.childShare);
  let elder = Math.round(n * cfg.newGame.elderShare);
  while (n - child - elder < minAdults && (child > 0 || elder > 0)) {
    if (child >= elder && child > 0) child--;
    else elder--;
  }
  return { child, adult: n - child - elder, elder };
}

export function startingBuildings(
  cfg: Config,
  n: number,
): { type: BuildingTypeId; count: number }[] {
  return [
    { type: 'shelter', count: Math.max(1, Math.round(n / cfg.newGame.peoplePerStartingShelter)) },
    { type: 'field', count: Math.max(1, Math.round(n / cfg.newGame.peoplePerStartingField)) },
  ];
}

function emptyState(cfg: Config, options: NewGameOptions, seed: string): GameState {
  const streams = seedStreams(seed);
  return {
    version: cfg.meta.stateVersion,
    seed,
    options: { ...options, seed },
    turn: 0,
    year: 1,
    season: cfg.seasons.order[0] ?? 'spring',
    rngState: streams.main,
    eventRngState: streams.events,
    nextIds: emptyIdCounters(),
    people: {},
    households: {},
    buildings: {},
    buildQueue: [],
    formerPeople: {},
    stocks: perResource((r) => ({ amount: 0, capacity: cfg.resources.baseCapacity[r] })),
    toolDurability: cfg.tools.startDurability,
    resourceHistory: perResource(() => emptyResourceHistory()),
    fieldsPlanted: 0,
    plantedPlots: {},
    seedReserved: 0,
    lastWorkedTurn: {},
    policies: structuredClone(cfg.policies.defaults),
    savedPriorityOrders: [],
    rationOverrides: perRationResource(() => emptyOverrides()),
    toolOverrides: {},
    triageOverrides: {},
    myHousehold: [],
    norms: emptyNorms(cfg),
    pendingPrompts: [],
    openPrompts: [],
    phaseCursor: null,
    promptAnswers: {},
    currentReport: null,
    history: [],
    historySummaries: [],
    series: emptySeries(),
    ledger: emptyLedger(),
    steady: { counter: 0, lastFailedCondition: null, lastResult: null },
    outcome: null,
    sandbox: false,
    decisionLog: [],
    cases: {},
    improvements: [],
    deterrence: [],
    factionMarks: [],
    festival: { lastHeldTurn: null, activeUntilTurn: null },
    quarantine: { active: false, untilTurn: null, contagionBoostUntilTurn: null },
    activeEffects: [],
    foreshadows: [],
    eventDraws: [],
    arrivalGroups: {},
    safety: { lastUndetectedKillingTurn: null, raidLosses: [] },
    counters: {
      lowTrustStreak: 0,
      lastArrivalTurn: null,
      lastRefusalTurn: null,
      gatesClosedUntilTurn: null,
      admissions: [],
      lastShortageTurn: null,
      refusingHouseholds: [],
    },
  };
}

function startingStocks(
  cfg: Config,
  state: GameState,
  options: NewGameOptions,
  people: Person[],
): void {
  const g = cfg.newGame;
  const seasons = g.stockSeasons[options.startingStock];
  let foodPerSeason = 0;
  let adults = 0;
  for (const p of people) {
    if (p.ageSeasons < cfg.people.adultAtSeasons) foodPerSeason += cfg.needs.foodChild;
    else if (p.ageSeasons >= cfg.people.elderAtSeasons) foodPerSeason += cfg.needs.foodElder;
    else {
      foodPerSeason += cfg.needs.foodAdult;
      adults++;
    }
  }
  const n = people.length;
  const amounts: Partial<Record<keyof GameState['stocks'], number>> = {
    food: seasons * foodPerSeason,
    wood: seasons * n * g.woodPerPersonSeason,
    water: g.waterSeasons * n * cfg.needs.waterPerPerson,
    tools: Math.round(adults * g.toolsPerAdult),
    medicine: g.startingMedicine,
    cloth: g.startingCloth,
    stone: g.startingStone,
    fibre: g.startingFibre,
  };
  // P-02: a start over its store by less than a sliver of a season of need is trimmed to the
  // store; a larger surplus is kept over capacity. The seed shares food's store but is sown in
  // the first Spring before that season's spoilage, so the food alone is measured. Starting
  // shelters and fields add no storage, so the base capacity is the store.
  const perSeason: Partial<Record<keyof GameState['stocks'], number>> = {
    food: foodPerSeason,
    wood: n * g.woodPerPersonSeason,
    water: n * cfg.needs.waterPerPerson,
  };
  for (const [r, amount] of Object.entries(amounts) as [keyof GameState['stocks'], number][]) {
    const stock = state.stocks[r];
    let start = Math.min(amount, stock.capacity * cfg.resources.hardCapFactor);
    const sliver = (perSeason[r] ?? 0) * g.trimOverCapacitySeasons;
    if (start > stock.capacity && start - stock.capacity < sliver) start = stock.capacity;
    stock.amount = start;
  }
}

/** Generate the founding settlement. Deterministic in (options, seed). */
export function generateSettlement(input: GenerateInput): GameState {
  const { cfg, options, seed, names } = input;
  const state = emptyState(cfg, options, seed);
  const handle = createRng(state.rngState);
  const rng = handle.rng;

  const counts = bandCounts(cfg, options.population);
  const people: Person[] = [];
  const bands: AgeBand[] = ['child', 'adult', 'elder'];
  for (const band of bands)
    for (let i = 0; i < counts[band]; i++) people.push(makePerson(cfg, state, input, band, rng));

  const adults = people.filter(
    (p) => p.ageSeasons >= cfg.people.adultAtSeasons && p.ageSeasons < cfg.people.elderAtSeasons,
  );
  enforceConstraints(cfg, adults, rng);

  const pools: Record<Sex, string[]> = {
    female: rng.shuffle(names.female),
    male: rng.shuffle(names.male),
  };
  const used = new Set<string>();
  for (const p of people) p.name = nameFor(p.sex, pools, used, rng, names);

  for (const p of people) state.people[p.id] = p;
  formHouseholds(cfg, state, people, rng);

  for (const { type, count } of startingBuildings(cfg, options.population)) {
    for (let i = 0; i < count; i++) {
      const id = mintId(state, 'building');
      state.buildings[id] = {
        id,
        type,
        condition: 100,
        builtTurn: 0,
        upkeepDueTurn: cfg.building.upkeepEverySeasons,
        frozen: false,
      };
    }
  }

  startingStocks(cfg, state, options, people);
  const fields = Object.values(state.buildings).filter((b) => b.type === 'field').length;
  state.seedReserved = fields * cfg.buildings.field.plots * cfg.jobs.farmer.seedPerPlot;
  state.rngState = handle.state();
  return state;
}

// ---- Section 14 (stream E2): arrivals. A group at the gate is generated like the founders (the
// same age bands, traits and stats, Mixed knowledge) from the event's own seed, then marked as
// newcomers. Candidates are not in `people` until admitted; their ids and names are taken now.

/** A name no one in the settlement, its past or at its gate already has. */
function arrivalName(sex: Sex, names: NameLists, used: Set<string>, rng: Rng): string {
  const pool = (sex === 'female' ? names.female : names.male).filter((n) => !used.has(n));
  let name: string;
  if (pool.length > 0) name = rng.pick(pool);
  else {
    const base = rng.pick(sex === 'female' ? names.female : names.male);
    let n = 2;
    while (used.has(`${base} ${n}`)) n++;
    name = `${base} ${n}`;
  }
  used.add(name);
  return name;
}

function usedNames(state: GameState): Set<string> {
  const used = new Set<string>();
  for (const p of Object.values(state.people)) used.add(p.name);
  for (const p of Object.values(state.formerPeople)) used.add(p.name);
  for (const g of Object.values(state.arrivalGroups))
    for (const p of g.candidates) used.add(p.name);
  return used;
}

function setSkill(p: Person, skill: SkillId, level: number): void {
  p.skills[skill] = level;
  p.skillPeaks[skill] = Math.max(p.skillPeaks[skill] ?? 0, level);
}

/**
 * Section 14: generate the group an arrival event brings (1 to 8 people with ages, skills,
 * traits, health and a reason). What the player can learn this season is recorded alongside:
 * claimed skills (true `claimTruth` of the time, else inflated) and who looks sick (a sick
 * arrival shows as sick `sickVisible` of the time). Deterministic in (state ids, spec).
 */
export function generateArrivalGroup(
  cfg: Config,
  state: GameState,
  names: NameLists,
  spec: ArrivalSpec,
  turn: number,
): ArrivalGroup {
  const m = cfg.migration;
  const rng = createRng(spec.seed).rng;
  const input: GenerateInput = {
    cfg,
    options: { ...state.options, startingKnowledge: 'mixed' },
    seed: state.seed,
    names,
  };
  const size = Math.max(1, Math.round(spec.size));
  const bands: AgeBand[] = [];
  for (let i = 0; i < size; i++) {
    if (spec.composition === 'adults' || i < 2) bands.push('adult');
    else if (rng.float() < m.refugeeChildShare) bands.push('child');
    else bands.push(rng.float() < 1 / 3 ? 'elder' : 'adult');
  }
  const candidates = bands.map((band) => makePerson(cfg, state, input, band, rng));
  const used = usedNames(state);
  const family = spec.composition === 'family' && size >= 2;
  if (family) {
    const [a, b] = candidates as [Person, Person];
    a.sex = 'female';
    b.sex = 'male';
    a.partnerId = b.id;
    b.partnerId = a.id;
    const minMotherSeasons = cfg.lifecycle.conceptionMinAgeYears * cfg.people.seasonsPerYear;
    for (const c of candidates.slice(2)) {
      if (c.ageSeasons >= cfg.people.adultAtSeasons) continue;
      c.ageSeasons = Math.max(0, Math.min(c.ageSeasons, a.ageSeasons - minMotherSeasons));
      c.parentIds = [a.id, b.id];
      a.childIds.push(c.id);
      b.childIds.push(c.id);
    }
  }
  const sickChance = spec.reason === 'famine' ? m.famineSickChance : m.arrivalSickChance;
  const jobs = (Object.keys(cfg.jobs) as (keyof Config['jobs'])[]).filter(
    (j): j is SkillId => j !== 'rest',
  );
  const claimedSkills: Record<PersonId, Partial<Record<SkillId, number>>> = {};
  const visiblySick: PersonId[] = [];
  for (const p of candidates) {
    p.name = arrivalName(p.sex, names, used, rng);
    p.origin = 'newcomer';
    p.bornTurn = turn - p.ageSeasons;
    if (spec.reason === 'famine' || spec.reason === 'raiders')
      p.health = Math.max(cfg.newGame.stats.healthMin, p.health - m.refugeeHealthLoss);
    const adult = p.ageSeasons >= cfg.people.adultAtSeasons;
    if (adult && spec.skilled !== null) {
      const skill = spec.skilled.skill ?? rng.pick(jobs);
      const level = spec.skilled.min + rng.float() * (spec.skilled.max - spec.skilled.min);
      setSkill(p, skill, Math.round(clamp(level, 0, cfg.skills.max) * 10) / 10);
    }
    if (rng.float() < sickChance) {
      const illnessId = rng.float() < 0.5 ? 'fever' : 'flux';
      p.status.sick = {
        illnessId,
        seasonsRemaining: cfg.illnesses[illnessId].untreated,
        seasonsSick: 0,
        treated: false,
        sinceTurn: turn,
      };
      if (rng.float() < m.sickVisible) visiblySick.push(p.id);
    }
    const claims: Partial<Record<SkillId, number>> = {};
    if (adult) {
      const best = (Object.entries(p.skills) as [SkillId, number][])
        .sort((x, y) => y[1] - x[1] || (x[0] < y[0] ? -1 : 1))
        .slice(0, m.claimedSkillCount);
      for (const [skill, level] of best) {
        const truthful = rng.float() < m.claimTruth;
        const claimed = truthful ? level : Math.min(cfg.skills.max, level + m.claimInflation);
        claims[skill] = Math.round(claimed * 10) / 10;
      }
      for (const [skill, level] of Object.entries(spec.claims ?? {}) as [SkillId, number][])
        claims[skill] = level;
    }
    claimedSkills[p.id] = claims;
  }
  return {
    id: mintId(state, 'group'),
    turn,
    reason: spec.reason,
    candidates,
    claimedSkills,
    visiblySick,
    status: 'pending',
    promptId: null,
    eventId: spec.eventId,
    admittedIds: [],
    decidedTurn: null,
    settled: false,
    family,
  };
}
